import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { authErrorMessages, type AuthErrorCode } from '@schwarz/contract';

import { LOGIN_START, loginPath } from '../../app/login-path.js';
import { BrandLogo } from '../../ui/BrandLogo.js';
import { IconArrow, IconLock } from '../../ui/icons.js';
import styles from './LoginPage.module.css';

/**
 * The sign-in card (S02, S03).
 *
 * Layout, copy and iconography match the approved mock's `Login.jsx` — the brand
 * lockup, the "Sign in" heading, the lock-and-arrow SSO button, the rule-flanked
 * divider and the footnote. The one difference is that the button is an anchor,
 * not a button: sign-in is a top-level navigation to the identity provider, and
 * the callback has to arrive as a first-party request for the session cookies to
 * be set.
 */
export function LoginPage(): JSX.Element {
  const [params] = useSearchParams();
  const error = params.get('error');
  // Set by the shell after a local sign-out, so the page can explain why the
  // next sign-in will not prompt for anything.
  const signedOutLocally = params.get('signedOut') === 'sso';
  const returnTo = params.get('returnTo') ?? '/';

  // Only relative destinations survive: an absolute URL here would turn sign-in
  // into an open redirect. The API enforces this too — defence in depth, not a
  // substitute for it.
  const safeReturnTo = returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/';
  const loginHref = loginPath(safeReturnTo, LOGIN_START);

  // The handshake. Clicking the button starts a navigation to the API, which
  // redirects to the identity provider — a real wait, and until now a blank
  // one. The mark works while it happens.
  const [handshaking, setHandshaking] = useState(false);
  const timer = useRef<number>();

  useEffect(() => () => window.clearTimeout(timer.current), []);

  function onSsoClick(event: React.MouseEvent<HTMLAnchorElement>): void {
    // Leave anything that is not a plain left click to the browser, so
    // middle-click and ctrl-click still open a tab the ordinary way.
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    event.preventDefault();
    setHandshaking(true);

    // A beat, so the ring is seen to turn before the page is replaced. This is
    // the one deliberate delay in the flow; the redirect chain that follows
    // takes several times longer, so it costs nothing anyone will notice.
    timer.current = window.setTimeout(() => window.location.assign(loginHref), 520);
  }

  return (
    <div className={styles.page}>
      <div className={`${styles.card} ${handshaking ? styles.cardBusy : ''}`}>
        <div className={styles.brand}>
          <BrandLogo size="big" maskId="ps-mark-login" busy={handshaking} />
        </div>

        <h2 className={styles.title}>Sign in</h2>
        <p className={styles.sub}>Use your organization account to continue.</p>

        {error && <AuthError code={error} />}

        {signedOutLocally && (
          <div className={styles.notice} role="status">
            <strong>Signed out of the console</strong>
            <span>
              Your organization sign-on is still open in this browser, so signing in again will not
              ask for your password. Close the browser to end it.
            </span>
          </div>
        )}

        <a
          className={`${styles.sso} ${handshaking ? styles.ssoBusy : ''}`}
          href={loginHref}
          onClick={onSsoClick}
          aria-disabled={handshaking}
        >
          <IconLock width="17" height="17" strokeWidth="2.2" strokeLinecap="round" />
          {handshaking ? 'Connecting…' : 'Continue with SSO'}
          <IconArrow width="16" height="16" strokeWidth="2.2" strokeLinecap="round" />
        </a>

        <div className={styles.divider}>single sign-on</div>
        <p className={styles.foot}>
          Authenticated via your identity provider using your corporate email.
        </p>
      </div>
    </div>
  );
}

/**
 * Turns the callback's error code into something actionable (S02: "errors are
 * clear and recoverable"). An unknown code still produces a sentence rather than
 * a blank space.
 */
function AuthError({ code }: { code: string }): JSX.Element {
  const known = code in authErrorMessages ? (code as AuthErrorCode) : null;
  const message = known ? authErrorMessages[known] : 'Sign-in did not complete. Please try again.';

  return (
    <div className={styles.error} role="alert">
      <strong>Sign-in was not completed</strong>
      <span>{message}</span>
    </div>
  );
}
